import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { AlertTriangle } from 'lucide-react';
import { useFecharInventario, useInventario } from '../hooks';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';

interface Props {
  open: boolean;
  inventoryId: string;
  onClose: () => void;
  onClosed: (summary: { alertsCreated: number; notFoundCount: number; divergentCount: number; foundCount: number }) => void;
}

/**
 * 2 etapas: (1) resumo prévio (contagens); (2) irreversibilidade + confirma.
 */
export function CloseInventoryModal({ open, inventoryId, onClose, onClosed }: Props) {
  const inv = useInventario(open ? inventoryId : undefined);
  const fechar = useFecharInventario();
  const [step, setStep] = useState<1 | 2>(1);
  const [error, setError] = useState<string | null>(null);
  const progress = inv.data?.inventory?.progress;

  async function handleConfirm() {
    setError(null);
    try {
      const res = await fechar.mutateAsync(inventoryId);
      onClosed({
        alertsCreated: res.alertsCreated,
        notFoundCount: res.notFoundCount,
        divergentCount: res.divergentCount,
        foundCount: res.foundCount,
      });
      setStep(1);
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setError(typeof msg === 'string' ? msg : 'Não foi possível fechar o inventário.');
    }
  }

  const pendingCount = progress?.pending ?? 0;
  const notFoundCount = progress?.not_found ?? 0;
  const divergentCount = progress?.divergent ?? 0;

  return (
    <Dialog open={open} onClose={() => { setStep(1); onClose(); }} className="relative z-40">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
          <Dialog.Title className="flex items-center gap-2 text-lg font-semibold text-red-800">
            <AlertTriangle className="h-5 w-5" /> Fechar inventário — etapa {step} de 2
          </Dialog.Title>

          {step === 1 && (
            <div className="mt-4 space-y-3">
              {inv.isLoading ? (
                <p className="text-sm text-gray-500">Carregando resumo...</p>
              ) : (
                <>
                  <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                    <p className="text-sm text-blue-900"><strong>{inv.data?.inventory.name}</strong></p>
                    <p className="mt-2 text-sm text-blue-900">
                      Progresso: {progress?.total ?? 0} itens ao todo.
                    </p>
                    <ul className="mt-1 space-y-0.5 text-xs text-blue-900">
                      <li>Localizados: <strong>{progress?.found ?? 0}</strong></li>
                      <li>Divergentes: <strong>{progress?.divergent ?? 0}</strong></li>
                      <li>Não localizados: <strong>{progress?.not_found ?? 0}</strong></li>
                      <li>Pendentes: <strong>{pendingCount}</strong></li>
                      <li>Não localizados após fechar: <strong>{notFoundCount + pendingCount}</strong></li>
                    </ul>
                  </div>
                  <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-xs text-yellow-900">
                    <p className="font-semibold">Antes de fechar o inventário</p>
                    <p className="mt-1">
                      Os {pendingCount} itens pendentes serão marcados como não localizados.
                      Os {notFoundCount + pendingCount} itens não localizados e as {divergentCount} divergências
                      serão sinalizados para análise. Confira os resultados antes de continuar.
                    </p>
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                <Button variant="danger" onClick={() => setStep(2)}>Avançar</Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="mt-4 space-y-3">
              <div className="rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-900">
                <strong>Atenção — operação irreversível.</strong> Inventário fechado fica <em>imutável</em> para
                fins de auditoria (RN004). Não é possível reabrir para novas conferências.
              </div>
              {error && <Alert variant="error">{error}</Alert>}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={() => setStep(1)}>Voltar</Button>
                <Button variant="danger" onClick={handleConfirm} loading={fechar.isPending}>
                  {fechar.isPending ? 'Fechando…' : 'Confirmar fechamento'}
                </Button>
              </div>
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
